import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Paper
} from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import DescriptionIcon from '@mui/icons-material/Description'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { type PrioritizationReportModel, type TimespanModel } from 'Pages/Reports/PrioritizationReport.types'
import { PrioritizationReportTableView } from 'Pages/Reports/PrioritizationReportTableView'
import { PrioritizationReportPDFView } from 'Pages/Reports/PrioritizationReportPDFView'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { AccountType } from 'core/types'
import { ReportHeader } from 'Pages/Reports/ReportHeader'

type ViewMode = 'table' | 'pdf'

export function PrioritizationReport (): JSX.Element {
  const account = useAccountId()
  const [reportData, setReportData] = useState<PrioritizationReportModel[]>([])
  const [loading, setLoading] = useState(false)
  const [timespans, setTimespans] = useState<TimespanModel[]>([])
  const [selectedTimespanID, setSelectedTimespanID] = useState<number | null>(null)
  const [timespanMenuAnchor, setTimespanMenuAnchor] = useState<null | HTMLElement>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [viewModeMenuAnchor, setViewModeMenuAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => {
    const fetchTimespans = async (): Promise<void> => {
      const { response, success } = await sendGet('/Report/PrioritizationReport/GetTimespans', {})
      if (success && Array.isArray(response)) {
        setTimespans(response)

        // Default to timespan that begins in current year
        const currentYear = new Date().getFullYear()
        const defaultTimespan = response.find((ts: TimespanModel) => {
          if (ts.startDate != null) {
            const startYear = new Date(ts.startDate).getFullYear()
            return startYear === currentYear
          }
          return false
        })

        if (defaultTimespan != null) {
          setSelectedTimespanID(defaultTimespan.id)
        } else if (response.length > 0) {
          // Fallback to first timespan if no match
          setSelectedTimespanID(response[0].id)
        }
      }
    }
    void fetchTimespans()
  }, [])

  // Auto-fetch report when component loads or filters change
  useEffect(() => {
    const fetchReport = async (): Promise<void> => {
      const isdID = account?.type === AccountType.ISD ? account?.id : account?.currentISDID

      if (isdID === undefined || isdID === null || selectedTimespanID === null) {
        return
      }

      const isdId: number = isdID
      const timespanId: number = selectedTimespanID

      setLoading(true)
      try {
        const url = `/Report/PrioritizationReport/GetPrioritizationReport?isdID=${isdId}&timespanID=${timespanId}`

        const { response, success } = await sendGet(url, {})

        if (success) {
          setReportData(response)
        } else {
          toast.error('Failed to fetch report data.')
        }
      } catch (error) {
        toast.error('An error occurred while fetching the report.')
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    void fetchReport()
  }, [account, selectedTimespanID])

  const selectedTimespan = timespans.find(ts => ts.id === selectedTimespanID)

  // Get short year format for badge (e.g., "26/27" from "School Year 2026/2027")
  const getShortYear = (timespanName: string): string => {
    const match = timespanName.match(/(\d{4})\/(\d{4})/)
    if (match != null) {
      return `${match[1].slice(-2)}/${match[2].slice(-2)}`
    }
    return timespanName.slice(0, 5)
  }

  // Get icon for current view mode
  const getViewIcon = (): JSX.Element => {
    switch (viewMode) {
      case 'table':
        return <TableChartIcon />
      case 'pdf':
        return <DescriptionIcon />
    }
  }

  return (
    <Box sx={{ p: 1 }}>
      {/* Title */}
      <ReportHeader title="Prioritization Report" />

      {/* Header Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Year selector dropdown */}
          <IconButton
            onClick={(e) => { setTimespanMenuAnchor(e.currentTarget) }}
            aria-label="Select School Year"
            sx={{
              border: '1px solid #E1E1E1',
              borderRadius: 1,
              width: 48,
              height: 48,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: '#F5F5F5'
              }
            }}
          >
            <Box sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1.2, textAlign: 'center' }}>
              {selectedTimespan != null ? getShortYear(selectedTimespan.name) : '26/27'}
            </Box>
          </IconButton>
          <Menu
            anchorEl={timespanMenuAnchor}
            open={Boolean(timespanMenuAnchor)}
            onClose={() => { setTimespanMenuAnchor(null) }}
          >
            {timespans.map((ts) => (
              <MenuItem
                key={ts.id}
                onClick={() => {
                  setSelectedTimespanID(ts.id)
                  setTimespanMenuAnchor(null)
                }}
                selected={ts.id === selectedTimespanID}
              >
                {ts.name}
              </MenuItem>
            ))}
          </Menu>

          {/* View mode selector dropdown */}
          {reportData.length > 0 && (
            <>
              <IconButton
                onClick={(e) => { setViewModeMenuAnchor(e.currentTarget) }}
                aria-label="Change View Mode"
                sx={{
                  border: '1px solid #E1E1E1',
                  borderRadius: 1,
                  width: 48,
                  height: 48,
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: '#F5F5F5'
                  }
                }}
              >
                {getViewIcon()}
              </IconButton>
              <Menu
                anchorEl={viewModeMenuAnchor}
                open={Boolean(viewModeMenuAnchor)}
                onClose={() => { setViewModeMenuAnchor(null) }}
              >
                <MenuItem
                  onClick={() => {
                    setViewMode('table')
                    setViewModeMenuAnchor(null)
                  }}
                  selected={viewMode === 'table'}
                >
                  <TableChartIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Table View
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setViewMode('pdf')
                    setViewModeMenuAnchor(null)
                  }}
                  selected={viewMode === 'pdf'}
                >
                  <DescriptionIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Report View
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box>
        {loading && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Loading report data...</Typography>
          </Paper>
        )}

        {!loading && reportData.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No data available for the selected timespan.</Typography>
          </Paper>
        )}

        {!loading && reportData.length > 0 && viewMode === 'table' && (
          <PrioritizationReportTableView
            reportData={reportData}
            loading={loading}
          />
        )}

        {!loading && reportData.length > 0 && viewMode === 'pdf' && (
          <PrioritizationReportPDFView
            reportData={reportData}
          />
        )}
      </Box>
    </Box>
  )
}
